import { apiClient, logger } from '@/lib/local-platform';
import type {
  MemberVerifyResult,
  VerifyPasscodeReq,
  ResourceTopic,
  RedeemRequest,
  RedeemResult,
  RedemptionRecordListResp,
  GetRedemptionRecordsReq,
  MemberListResp,
  GetMemberListReq,
  CreateMemberReq,
  UpdateMemberReq,
  Member,
  MemberTypeConfigListResp,
  CreateMemberTypeConfigReq,
  UpdateMemberTypeConfigReq,
  CreateMemberTypeConfigResp,
  DeleteMemberTypeConfigResp,
  MemberAdminDetailResp,
  UpdateMemberResp,
} from '@shared/api.interface';

function getAdminHeaders() {
  const token = sessionStorage.getItem('admin_token');
  return token ? { 'x-admin-token': token } : {};
}

export async function verifyPasscode(data: VerifyPasscodeReq): Promise<MemberVerifyResult> {
  try {
    const response = await apiClient({
      url: '/api/members/verify',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error) {
    logger.error('验证兑换码失败', error);
    throw error;
  }
}

export async function getActiveTopics(): Promise<ResourceTopic[]> {
  try {
    const response = await apiClient({
      url: '/api/members/topics',
      method: 'GET',
    });
    return response.data;
  } catch (error) {
    logger.error('获取主题列表失败', error);
    throw error;
  }
}

export async function redeemResource(data: RedeemRequest): Promise<RedeemResult> {
  try {
    const response = await apiClient({
      url: '/api/members/redeem',
      method: 'POST',
      data,
    });
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    if (!status || status >= 500) {
      logger.error('领取资源失败', error);
    }
    throw error;
  }
}

export async function trackTopicView(topicName: string): Promise<void> {
  try {
    await apiClient({
      url: `/api/members/topics/${encodeURIComponent(topicName)}/view`,
      method: 'POST',
    });
  } catch (error) {
    logger.error('记录主题浏览次数失败', error);
  }
}

export async function getRedemptions(params: GetRedemptionRecordsReq): Promise<RedemptionRecordListResp> {
  try {
    const response = await apiClient({
      url: '/api/members/redemptions',
      method: 'GET',
      params,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取领取记录失败', error);
    throw error;
  }
}

export async function getMembers(params: GetMemberListReq): Promise<MemberListResp> {
  try {
    const response = await apiClient({
      url: '/api/members',
      method: 'GET',
      params,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取会员列表失败', error);
    throw error;
  }
}

export async function getMemberByPasscode(passcode: string): Promise<Member | null> {
  try {
    const response = await apiClient({
      url: '/api/members/detail',
      method: 'GET',
      params: { passcode },
    });
    return response.data;
  } catch (error) {
    logger.error('获取会员详情失败', error);
    throw error;
  }
}

export async function getAdminMemberDetail(passcode: string): Promise<MemberAdminDetailResp> {
  try {
    const response = await apiClient({
      url: '/api/members/admin/detail',
      method: 'GET',
      params: { passcode },
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取管理员会员详情失败', error);
    throw error;
  }
}

export async function createMember(data: CreateMemberReq): Promise<void> {
  try {
    await apiClient({
      url: '/api/members',
      method: 'POST',
      data,
      headers: getAdminHeaders(),
    });
  } catch (error) {
    logger.error('创建会员失败', error);
    throw error;
  }
}

export async function updateMember(data: { passcode: string } & UpdateMemberReq): Promise<UpdateMemberResp> {
  try {
    const response = await apiClient({
      url: '/api/members/update',
      method: 'POST',
      data,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('更新会员失败', error);
    throw error;
  }
}

export async function getMemberTypeConfigs(): Promise<MemberTypeConfigListResp> {
  try {
    const response = await apiClient({
      url: '/api/members/type-configs',
      method: 'GET',
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('获取会员类型配置失败', error);
    throw error;
  }
}

export async function createMemberTypeConfig(data: CreateMemberTypeConfigReq): Promise<CreateMemberTypeConfigResp> {
  try {
    const response = await apiClient({
      url: '/api/members/type-configs',
      method: 'POST',
      data,
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('创建会员类型配置失败', error);
    throw error;
  }
}

export async function updateMemberTypeConfig(id: string, data: UpdateMemberTypeConfigReq): Promise<void> {
  try {
    await apiClient({
      url: `/api/members/type-configs/${id}`,
      method: 'PUT',
      data,
      headers: getAdminHeaders(),
    });
  } catch (error) {
    logger.error('更新会员类型配置失败', error);
    throw error;
  }
}

export async function deleteMemberTypeConfig(id: string): Promise<DeleteMemberTypeConfigResp> {
  try {
    const response = await apiClient({
      url: `/api/members/type-configs/${id}`,
      method: 'DELETE',
      headers: getAdminHeaders(),
    });
    return response.data;
  } catch (error) {
    logger.error('删除会员类型配置失败', error);
    throw error;
  }
}
